import React, { useContext, useState } from "react";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import { TranslationContext, StyleTypeContext } from "../../../umbilical_tools";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import { Formik, Form } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useCallback } from "react";
import {
  getRestaurantCategories,
  restaurantSearch,
  restaurantSearchFormUpdate,
} from "../../../actions/search/restaurants_search";
import { useEffect } from "react";
import { submitPayloadFromValues } from "./helpers";
import {
  SelectField,
  DestRestAutoCompleteInputField,
} from "../../forms/inputGroup/helpers";
import jssVariables from "../../../jss_setup";
import { useApoAuth } from "../../../hooks";
import PropTypes from "prop-types";
import { SearchButton } from "../../common/Button";

const styles = createUseStyles({
  restaurantsSearchFormContainer: (props) => ({
    "& .rs-nav-waterline": {
      display: "none",
    },
    padding: jssVariables.normal_gap,
    paddingTop: 0,
    ..._.get(props, "customStyles.restaurantsSearchFormContainer", {}),
  }),
  form: (props) => ({
    paddingTop: jssVariables.normal_gap,
    border: `${props.borderSize} solid ${props.baseColor}`,
    borderRadius: props.borderRadius,
    display: "grid",
    gridGap: jssVariables.half_gap,
    background: props.background,
    ..._.get(props, "customStyles.form", {}),
  }),
  fields: (props) => ({
    gridGap: jssVariables.half_gap,
    display: "grid",
    ..._.get(props, "customStyles.fields", {}),
  }),
  categoryContainer: (props) => ({
    ..._.get(props, "customStyles.categoryContainer", {}),
  }),
  searchBtnContr: (props) => ({
    justifySelf: "center",
    paddingTop: jssVariables.normal_gap,
    ..._.get(props, "customStyles.searchBtnContainer", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    restaurantsSearchFormContainer: (props) => ({
      transform: "unset",
      background: "unset",
      width: "100%",
      bottom: 0,
      padding: [jssVariables.normal_gap, 0],
      ..._.get(
        props,
        "customStyles.restaurantsSearchFormContainer.smallscreen",
        {}
      ),
    }),
    form: (props) => ({
      gridTemplateColumns: "1fr max-content",
      alignItems: "end",
      padding: 0,
      ..._.get(props, "customStyles.form.smallscreen", {}),
    }),
    fields: (props) => ({
      display: "grid",
      gridTemplateColumns: "3fr 2fr repeat(2, 7rem)",
      alignItems: "start",
      ..._.get(props, "customStyles.fields.smallscreen", {}),
    }),
    categoryContainer: (props) => ({
      ..._.get(props, "customStyles.categoryContainer.smallscreen", {}),
    }),
    searchBtnContr: (props) => ({
      display: "grid",
      justifyContent: "center",
      ..._.get(props, "customStyles.searchBtnContainer.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    restaurantsSearchFormContainer: (props) => ({
      ..._.get(
        props,
        "customStyles.restaurantsSearchFormContainer.bigscreen",
        {}
      ),
    }),
  },
});
const RestaurantsSearchForm = ({ customStyles }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { brand } = useContext(StyleTypeContext);
  const { t } = useContext(TranslationContext);
  const history = useHistory();

  const { website_api_key } = useApoAuth();

  const { search_form, categories } = useSelector((state) => {
    return {
      search_form: _.get(state, "RestaurantSearchForm", {}),
      categories: _.get(state, "RestaurantCategories.categories", []),
    };
  });
  const classes = styles({
    customStyles,
  });
  function getPriceQuartileSymbol({ symbol = "$", index }) {
    const priceSymbols = [
      [1, symbol],
      [2, symbol.repeat(2)],
      [3, symbol.repeat(3)],
      [4, symbol.repeat(4)],
    ];
    if (index) {
      return priceSymbols[index + 1][1];
    }
    return priceSymbols;
  }
  const priceQuartile = getPriceQuartileSymbol({
    symbol: brand === "tmdh" ? "£" : "$",
  });

  const orderByOptions = [
    ["price", t("price")],
    ["score", t("score")],
  ];

  const dispatch = useDispatch();
  const fetchCategories = useCallback(
    () => dispatch(getRestaurantCategories()),
    [dispatch]
  );
  const onSearchFormUpdate = useCallback(
    (form) => dispatch(restaurantSearchFormUpdate(form)),
    [dispatch]
  );
  const onSearch = useCallback(() => {
    dispatch(restaurantSearch({ newSearch: true }));
  }, [dispatch]);
  async function onFormSearch() {
    history.push("/dining");
    await onSearch();
  }

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return (
    <Formik
      initialValues={{
        destinationObj: _.get(search_form, "destinationObj", {}),
        category: _.get(search_form, "category", ""),
        price_quartile: _.get(search_form, "price_quartile", ""),
        order_by: _.get(search_form, "order_by", "score"),
      }}
      onSubmit={async (values) => {
        if (!isSubmitting) {
          setIsSubmitting(true);
          onSearchFormUpdate(
            submitPayloadFromValues({
              ...values,
              website_api_key,
            })
          );
          onFormSearch();
        }
      }}>
      {({ values }) => (
        <div className={classes.restaurantsSearchFormContainer}>
          <Form className={classes.form}>
            <div className={classes.fields}>
              <DestRestAutoCompleteInputField
                name="destinationObj"
                label="Destination/Restaurant"
                types={["DEST"]}
                product_type={"REST"}
                placeholder={t("destinationPlaceholder")}
                autocomplete={true}
                value={
                  values.destinationObj.destination_meta ||
                  values.destinationObj.restaurant_meta
                }
                isSideForm={false}
                customStyles={{
                  ..._.get(customStyles, "inputGroupField", {}),
                }}
              />
              <div className={classes.categoryContainer}>
                <SelectField
                  label={t("category")}
                  name="category"
                  placeholder="----"
                  options={categories}
                  customStyles={{
                    ..._.get(customStyles, "inputGroupField", {}),
                  }}
                />
              </div>
              <SelectField
                label={t("price")}
                name="price_quartile"
                placeholder="----"
                options={priceQuartile}
                customStyles={{
                  ..._.get(customStyles, "inputGroupField", {}),
                }}
              />
              <SelectField
                label={t("orderBy")}
                name="order_by"
                placeholder="----"
                options={orderByOptions}
                noTranslInLabel={true}
                customStyles={{
                  ..._.get(customStyles, "inputGroupField", {}),
                }}
              />
            </div>
            <div className={classes.searchBtnContr}>
              <SearchButton
                text={t("search")}
                loading={isSubmitting}
                customStyles={_.get(customStyles, "searchBtn", {})}
              />
            </div>
          </Form>
        </div>
      )}
    </Formik>
  );
};
RestaurantsSearchForm.defaultProps = {
  customStyles: {},
};
RestaurantsSearchForm.propTypes = {
  customStyles: PropTypes.object,
  children: PropTypes.node,
};
export default RestaurantsSearchForm;
